import { useCallback, useRef, useState } from 'react';
import api from '../../../../api/api';
import { INITIAL_SEARCH_PARAMS } from '../constants/invoiceConstants';
import { formatBuyerColumnDisplay, validateInvoiceSelection, createInvoiceGroups } from '../utils/invoiceUtils';
import { downloadIndicatorRef } from '../../../../utils/excelDownload';

export const useInvoiceHandlers = (
    t,
    invoiceData,
    selectedItems,
    setAlertInfo,
    setInvoiceDialogOpen,
    setSelectedItems,
    fetchInvoiceData,
    markInvoiceSearchForceRefresh,
    setSearchParams,
    setPage,
    setInvoiceData,
    setTotalCount,
    setHasSearched,
    searchParamsRef,
    isLoading,
    setSearchParamsToSend,
    setInvoiceTableSortConfig,
    clearInvoiceIssuanceStorage,
    getInvoiceIssuanceListFetchSnapshot,
    totalCount,
    setNeedToFetch
) => {
    const isIssuingRef = useRef(false);
    const [isIssuing, setIsIssuing] = useState(false);

    const handleSearch = useCallback(() => {
        markInvoiceSearchForceRefresh?.();
        setInvoiceTableSortConfig?.({ key: null, direction: null });
        setSearchParamsToSend({ ...(searchParamsRef?.current || {}) });
        setPage(0);
        setSelectedItems([]);
        setHasSearched(true);
        setNeedToFetch?.(true);
    }, [
        setPage,
        setHasSearched,
        searchParamsRef,
        setSearchParamsToSend,
        setInvoiceTableSortConfig,
        setSelectedItems,
        markInvoiceSearchForceRefresh,
        setNeedToFetch
    ]);

    const handleReset = useCallback(() => {
        clearInvoiceIssuanceStorage?.();
        setSearchParams(INITIAL_SEARCH_PARAMS);
        setSearchParamsToSend(INITIAL_SEARCH_PARAMS);
        setInvoiceTableSortConfig?.({ key: null, direction: null });
        setPage(0);
        setHasSearched(false);
        setSelectedItems([]);
        setNeedToFetch?.(false);
    }, [
        clearInvoiceIssuanceStorage,
        setSearchParams,
        setSearchParamsToSend,
        setPage,
        setHasSearched,
        setSelectedItems,
        setInvoiceTableSortConfig,
        setNeedToFetch
    ]);

    const handleInvoiceIssuance = useCallback(() => {
        if (selectedItems.length === 0) {
            setAlertInfo({
                open: true,
                title: t('invoice.warning'),
                message: t('invoice.selectItems'),
                severity: 'warning'
            });
            return;
        }

        const selectedData = invoiceData.filter(item => selectedItems.includes(item.id));
        const validation = validateInvoiceSelection(selectedData, t);

        if (!validation.isValid) {
            if (validation.hasExistingInvoice) {
                setAlertInfo({
                    ...validation.alertInfo,
                    onConfirm: () => {
                        setAlertInfo(prev => ({ ...prev, open: false }));
                        setInvoiceDialogOpen(true);
                    },
                    onClose: () => {
                        setAlertInfo(prev => ({ ...prev, open: false }));
                    }
                });
            } else {
                setAlertInfo(validation.alertInfo);
            }
            return;
        }

        setInvoiceDialogOpen(true);
    }, [selectedItems, invoiceData, t, setAlertInfo, setInvoiceDialogOpen]);

    const performInvoiceIssuance = useCallback(async (vatOption, deliveryDateByRowKey, dialogSelectedRowKeys) => {
        setIsIssuing(true);

        try {
            const baseData = invoiceData.filter(item => selectedItems.includes(item.id));
            const selectedData = (dialogSelectedRowKeys && Array.isArray(dialogSelectedRowKeys))
                ? (() => {
                    const keySet = new Set(dialogSelectedRowKeys);
                    return baseData.filter((item) => {
                        const rowKey = item.id || `${item.main_order_id}-${item.sub_order_id}`;
                        return keySet.has(rowKey);
                    });
                })()
                : baseData;
            if (selectedData.length === 0) {
                setAlertInfo({
                    open: true,
                    title: t('error.title'),
                    message: t('invoice.error.noItemSelected', '발급할 항목이 없습니다.'),
                    severity: 'warning'
                });
                return;
            }
            const invoiceGroups = createInvoiceGroups(selectedData, vatOption, deliveryDateByRowKey);

            const results = [];
            for (const [key, groupData] of Object.entries(invoiceGroups)) {
                try {
                    const response = await api.post('/invoice/v1/invoice/item', groupData);

                    try {
                        const newInvoiceId = response?.data?.invoice_id;
                        const newInvoiceNo = response?.data?.invoice_no;
                        if (newInvoiceId && Array.isArray(groupData?.items)) {
                            const STORAGE_KEY = 'invoiceLineMap';
                            const raw = localStorage.getItem(STORAGE_KEY);
                            const map = raw ? JSON.parse(raw) : {};
                            map[String(newInvoiceId)] = {
                                invoice_no: newInvoiceNo,
                                lines: groupData.items.map((it) => ({
                                    gi_request_id: it.gi_request_id,
                                    gi_request_line_id: it.gi_request_line_id,
                                    sub_order_id: it.sub_order_id,
                                    quantity: it.quantity
                                })),
                                createdAt: new Date().toISOString()
                            };
                            localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
                        }
                    } catch (e) {
                        console.warn('invoiceLineMap 저장 실패:', e?.message);
                    }

                    if (response.data) {
                        results.push({
                            success: true,
                            invoiceNo: response.data.invoice_no,
                            amount: response.data.amount,
                            currency: response.data.currency
                        });
                    }
                } catch (error) {
                    console.error(`인보이스 발급 실패 (${key}):`, error);
                    results.push({
                        success: false,
                        error: error.message
                    });
                }
            }

            const successCount = results.filter(r => r.success).length;
            const resultTotal = results.length;

            if (successCount === resultTotal) {
                setSelectedItems([]);
                setInvoiceDialogOpen(false);
                const snap = getInvoiceIssuanceListFetchSnapshot?.() || {};
                await fetchInvoiceData(
                    snap.searchParams ?? searchParamsRef?.current ?? INITIAL_SEARCH_PARAMS,
                    setHasSearched,
                    {
                        forceRefresh: true,
                        silent: true,
                        ...(snap.listOptions || {})
                    }
                );
            } else if (successCount > 0) {
                setAlertInfo({
                    open: true,
                    title: t('common.warning', '경고'),
                    message: `${successCount}/${resultTotal} ${t('invoice.partialIssuanceSuccess')}`,
                    severity: 'warning'
                });
                setSelectedItems([]);
                setInvoiceDialogOpen(false);
                const snap = getInvoiceIssuanceListFetchSnapshot?.() || {};
                await fetchInvoiceData(
                    snap.searchParams ?? searchParamsRef?.current ?? INITIAL_SEARCH_PARAMS,
                    setHasSearched,
                    {
                        forceRefresh: true,
                        silent: true,
                        ...(snap.listOptions || {})
                    }
                );
            } else {
                setAlertInfo({
                    open: true,
                    title: t('error.title'),
                    message: t('invoice.issuanceError'),
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('인보이스 발급 실패:', error);
            setAlertInfo({
                open: true,
                title: t('error.title'),
                message: t('invoice.issuanceError'),
                severity: 'error'
            });
        } finally {
            isIssuingRef.current = false;
            setIsIssuing(false);
        }
    }, [
        invoiceData,
        selectedItems,
        t,
        setAlertInfo,
        setSelectedItems,
        setInvoiceDialogOpen,
        fetchInvoiceData,
        searchParamsRef,
        setHasSearched,
        getInvoiceIssuanceListFetchSnapshot
    ]);

    const handleInvoiceConfirm = useCallback((vatOption = 'tax_free', deliveryDateByRowKey = {}, dialogSelectedRowKeys = null) => {
        if (isIssuingRef.current) {
            return;
        }
        isIssuingRef.current = true;
        setAlertInfo({
            open: true,
            title: t('invoice.dialog.confirmTitle'),
            message: t('invoice.dialog.confirmMessage'),
            severity: 'warning',
            isConfirm: false,
            confirmText: t('invoice.dialog.confirmYes'),
            cancelText: t('invoice.dialog.confirmNo'),
            onConfirm: () => {
                setAlertInfo((prev) => ({ ...prev, open: false, onConfirm: null, onClose: null }));
                performInvoiceIssuance(vatOption, deliveryDateByRowKey, dialogSelectedRowKeys);
            },
            onClose: () => {
                isIssuingRef.current = false;
                setAlertInfo((prev) => ({ ...prev, open: false, onConfirm: null, onClose: null }));
            }
        });
    }, [t, setAlertInfo, performInvoiceIssuance]);

    const exportToExcel = useCallback(() => {
        if (invoiceData.length === 0) {
            setAlertInfo({
                open: true,
                title: t('error.title'),
                message: t('invoice.error.noDataToExport'),
                severity: 'warning'
            });
            return;
        }

        try {
            if (downloadIndicatorRef?.current) {
                downloadIndicatorRef.current.startDownload();
            }

            const headers = [
                'File No',
                'Customer',
                'Buyer',
                'Style No',
                'Item',
                'Size',
                'Color',
                'Quantity',
                'Unit Price',
                'Amount',
                'Currency',
                'Delivery',
                'Invoice No'
            ];

            const csvContent = [
                headers.join(','),
                ...invoiceData.map(item => [
                    item.file_no || '',
                    item.vendor_name || '',
                    formatBuyerColumnDisplay(item.buyer_name, item.buyer_detail) || '',
                    item.style_no || '',
                    item.thread_name || '',
                    item.size_name || '',
                    item.color || '',
                    item.qty_requested ?? item.qty_delivered ?? '',
                    item.price ?? '',
                    item.amount ?? '',
                    item.price_currency || '',
                    item.delivery_name || '',
                    item.invoice_no || item.existing_invoice_no || ''
                ].join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `invoice_issuance_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            if (downloadIndicatorRef?.current) {
                setTimeout(() => {
                    downloadIndicatorRef.current.completeDownload();
                }, 100);
            }

            const total = Number.isFinite(totalCount) ? totalCount : invoiceData.length;
            setAlertInfo({
                open: true,
                title: t('success.title'),
                message: t('invoice.success.exportCompletedWithCount', {
                    defaultValue: `Export completed (loaded {{loaded}} / total {{total}})`,
                    loaded: invoiceData.length,
                    total
                }),
                severity: 'success'
            });
        } catch (error) {
            console.error('Excel 내보내기 중 오류:', error);
            if (downloadIndicatorRef?.current) {
                downloadIndicatorRef.current.completeDownload();
            }
            setAlertInfo({
                open: true,
                title: t('error.title'),
                message: t('error.export'),
                severity: 'error'
            });
        }
    }, [invoiceData, setAlertInfo, t, totalCount]);

    return {
        handleSearch,
        handleReset,
        handleInvoiceIssuance,
        handleInvoiceConfirm,
        exportToExcel,
        isIssuing
    };
};
